import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import { ReactComponent as XCircleIcon } from '@/assets/icons/x-circle.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import VacationStatusDot from '@/pages/Freelancer/components/VacationStatusDot';
import VacationTypeBadge from '@/pages/Freelancer/components/VacationTypeBadge';
import { FreelancerHolidayRequestResource } from '@/types/resources';
import { api } from '@/utils/api';

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 150px;
`;

const GrayTd = styled(Td)`
  color: ${({ theme }) => theme.colors.gray};
  padding: 20px;
`;

interface Props {
  request: FreelancerHolidayRequestResource;
  isPending?: boolean;
}
const ApprovalsTableRow: FC<Props> = ({ request, isPending }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const vacationPeriod = `${request.startDate ? DateTime.fromISO(request.startDate).toFormat('yyyy/LL/dd') : '-'} - ${request.endDate ? DateTime.fromISO(request.endDate).toFormat('yyyy/LL/dd') : '-'}`;
  const deductedDaysText = `${request.totalDays} ${request.totalDays === 1 ? 'day' : 'days'}`;

  const handleCancel = async () => {
    const confirmed = await alerts.confirm(
      'Are You sure You want to cancel time off?',
    );
    if (!confirmed) {
      return;
    }
    try {
      await api.delete(
        `/v1/payments/freelancers/holidays/requests/${request.id}`,
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });
      alerts.success('Vacation request canceled successfully');
    } catch (e) {
      alerts.error('Failed to cancel vacation request');
    }
  };

  return (
    <tr>
      <GrayTd>
        {DateTime.fromISO(request.requestedAt).toFormat('yyyy/LL/dd') || '-'}
      </GrayTd>
      <Td>{vacationPeriod}</Td>
      <Td>{deductedDaysText}</Td>
      <Td>
        <VacationTypeBadge type={request.type} />
      </Td>
      <Td>
        <VacationStatusDot status={request.teamLeadStatus} />
      </Td>
      <Td>
        <VacationStatusDot status={request.hrStatus} />
      </Td>
      <Td $actionsCol>
        {isPending && (
          <ActionsWrap>
            <StyledActionsDropdown>
              <ActionsDropdownButton as="button" onClick={handleCancel}>
                <XCircleIcon />
                Cancel time off
              </ActionsDropdownButton>
            </StyledActionsDropdown>
          </ActionsWrap>
        )}
      </Td>
    </tr>
  );
};

export default ApprovalsTableRow;
